import ApiClient from '@/api/ApiClient';

import type {
  CaseConfiguration,
  PagedResult,
  RegistryRecord,
} from '../models';

export type GetCaseReportRequest = {
  id: string;
  templateId: string;
  lang?: string;
  view?: string;
};

export class CasesviewClient {
  apiClient: ApiClient;

  constructor(apiClient?: ApiClient) {
    this.apiClient = apiClient || ApiClient.instance;
  }

  getCaseView(request: { id: string; }): Promise<CaseConfiguration> {
    const { id } = request;
    return this.apiClient.get({
      path: `/api/cases/${id}`,
    });
  }

  startCaseAction(request: { actionId: string; id: string; }): Promise<void> {
    const { actionId, id } = request;
    return this.apiClient.post({
      path: `/api/cases/${id}/actions/${actionId}`,
    });
  }

  getCaseActionVariables(request: { actionId: string; id: string; recordKey?: string; }): Promise<Record<string, unknown>> {
    const { actionId, id, ...params } = request;
    return this.apiClient.get({
      path: `/api/cases/${id}/actions/${actionId}/variables`,
      params,
    });
  }

  exportRecords(request: { id: string; lang?: string; view?: string; }): Promise<void> {
    const { id, ...params } = request;
    return this.apiClient.get({
      path: `/api/cases/${id}/export`,
      params,
    });
  }

  getCaseViewRecordById(request: { id: string; recordId?: string; view?: string; }): Promise<RegistryRecord> {
    const { id, ...params } = request;
    return this.apiClient.get({
      path: `/api/cases/${id}/record`,
      params,
    });
  }

  updateCaseRecord(request: { id: string; recordId: string; body?: Record<string, unknown>; }): Promise<void> {
    const { id, recordId, body } = request;
    return this.apiClient.put({
      path: `/api/cases/${id}/record/${recordId}`,
      body,
    });
  }

  getCaseViewRecords(request: { id: string; view?: string; }): Promise<PagedResult> {
    const { id, ...params } = request;
    return this.apiClient.get({
      path: `/api/cases/${id}/records`,
      params,
    });
  }

  importCaseViewRecords(request: { id: string; importer: string; body?: Record<string, unknown>; }): Promise<void> {
    const { id, importer, body } = request;
    return this.apiClient.post({
      path: `/api/cases/${id}/records/${importer}`,
      body,
    });
  }

  getCaseReport(request: GetCaseReportRequest): Promise<void> {
    const { id, templateId, ...params } = request;
    return this.apiClient.get({
      path: `/api/cases/${id}/records/${templateId}`,
      params,
    });
  }

  proxyStartCaseAction(request: { actionId: string; id: string; }): Promise<void> {
    const { actionId, id } = request;
    return this.apiClient.post({
      path: `/api/public/cases/${id}/actions/${actionId}`,
    });
  }

  proxyGetCaseViewRecords(request: { id: string; view?: string; }): Promise<PagedResult> {
    const { id, ...params } = request;
    return this.apiClient.get({
      path: `/api/public/cases/${id}/records`,
      params,
    });
  }
}

export default CasesviewClient;
